import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Minus, Plus, Trash2, ShoppingBag, AlertCircle, ArrowLeft, Truck } from 'lucide-react';
import { useCart } from '@/context/CartContext';
import { useSettings } from '@/context/SettingsContext';
import api from '@/lib/api';

export const CartDrawer = () => {
  const { items, isOpen, setIsOpen, updateQuantity, removeItem, totalAmount, clearCart } = useCart();
  const navigate = useNavigate();
  const { settings: s } = useSettings();
  const [minOrderValue, setMinOrderValue] = useState(20);
  const [serviceFee, setServiceFee] = useState(5);

  useEffect(() => {
    if (isOpen) {
      api.get('/shop-settings').then(r => {
        setMinOrderValue(r.data.min_order_value || 20);
        setServiceFee(r.data.service_fee || 5);
      }).catch(() => {});
    }
  }, [isOpen]);

  // Mindestbestellwert bezieht sich nur auf Produktsumme, NICHT auf Servicepauschale
  const isBelowMinimum = totalAmount < minOrderValue && items.length > 0;
  const totalWithServiceFee = totalAmount + (items.length > 0 ? serviceFee : 0);

  const handleCheckout = () => {
    setIsOpen(false);
    navigate('/bestellen');
  };

  // Weiter einkaufen - schließt nur den Drawer, Warenkorb bleibt erhalten
  const handleContinueShopping = () => {
    setIsOpen(false);
  };

  return (
    <Sheet open={isOpen} onOpenChange={setIsOpen}>
      <SheetContent className="w-full sm:max-w-lg bg-white flex flex-col" data-testid="cart-drawer">
        <SheetHeader>
          <SheetTitle className="text-[#0E518D] flex items-center gap-2">
            <ShoppingBag className="h-5 w-5" />
            {s.cart_title || 'Ihre Auswahl'}
          </SheetTitle>
        </SheetHeader>

        <div className="mt-6 flex-1 overflow-y-auto">
          {items.length === 0 ? (
            <div className="text-center py-12" data-testid="cart-empty">
              <ShoppingBag className="h-12 w-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">{s.cart_empty || 'Ihr Warenkorb ist leer'}</p>
              <p className="text-slate-400 text-xs mt-1">{s.cart_empty_sub || ''}</p>
              {/* Weiter einkaufen Button auch bei leerem Warenkorb */}
              <Button 
                variant="outline" 
                onClick={handleContinueShopping} 
                className="mt-6 border-[#0E518D] text-[#0E518D] hover:bg-[#0E518D]/5 rounded-sm"
                data-testid="continue-shopping-empty"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Weiter einkaufen
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {items.map(item => (
                <div key={item.product_id} className="bg-slate-50 rounded-sm p-4" data-testid={`cart-item-${item.product_id}`}>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex-1 min-w-0 mr-3">
                      <p className="font-semibold text-[#1E293B] text-sm truncate">{item.name}</p>
                      <p className="text-xs text-slate-400">Art. {item.article_number}</p>
                    </div>
                    <div className="flex items-start gap-2">
                      {/* Preis rechtsbündig mit fester Breite */}
                      <div className="text-right min-w-[70px]">
                        <p className="text-xs text-slate-400 whitespace-nowrap">{item.price.toFixed(2)} € x {item.quantity}</p>
                        <p className="font-bold text-[#0E518D] text-sm whitespace-nowrap tabular-nums">{item.total.toFixed(2)} €</p>
                      </div>
                      <button 
                        type="button"
                        onClick={() => removeItem(item.product_id)} 
                        className="text-slate-400 hover:text-red-500 transition-colors p-1 flex-shrink-0" 
                        data-testid={`remove-item-${item.product_id}`}
                        aria-label="Entfernen"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center">
                    <div className="flex items-center gap-2">
                      <button 
                        type="button"
                        onClick={() => updateQuantity(item.product_id, item.quantity - 1)} 
                        className="w-8 h-8 rounded-sm border border-slate-200 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200" 
                        data-testid={`qty-minus-${item.product_id}`}
                        aria-label="Menge verringern"
                      >
                        <Minus className="h-3 w-3" />
                      </button>
                      <span className="text-sm font-medium w-8 text-center">{item.quantity}</span>
                      <button 
                        type="button"
                        onClick={() => updateQuantity(item.product_id, item.quantity + 1)} 
                        className="w-8 h-8 rounded-sm border border-slate-200 flex items-center justify-center hover:bg-slate-100 active:bg-slate-200" 
                        data-testid={`qty-plus-${item.product_id}`}
                        aria-label="Menge erhöhen"
                      >
                        <Plus className="h-3 w-3" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {items.length > 0 && (
          <div className="mt-auto pt-4 space-y-3 border-t border-slate-100">
            {/* Produktsumme */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500">Produkte</span>
              <span className="font-medium text-slate-700 tabular-nums min-w-[70px] text-right">{totalAmount.toFixed(2)} €</span>
            </div>
            
            {/* Servicepauschale */}
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-500 flex items-center gap-1">
                <Truck className="h-3.5 w-3.5" />
                Liefer- und Servicepauschale
              </span>
              <span className="font-medium text-slate-700 tabular-nums min-w-[70px] text-right">{serviceFee.toFixed(2)} €</span>
            </div>
            
            <Separator />
            
            {/* Gesamtsumme */}
            <div className="flex justify-between items-center">
              <span className="font-semibold text-slate-700">Gesamtsumme</span>
              <span className="text-xl font-bold text-[#0E518D] tabular-nums min-w-[90px] text-right">{totalWithServiceFee.toFixed(2)} €</span>
            </div>
            
            {/* Mindestbestellwert Hinweis - bezieht sich auf Produktsumme */}
            <div className={`p-3 rounded-sm ${isBelowMinimum ? 'bg-amber-50 border border-amber-200' : 'bg-green-50 border border-green-200'}`} data-testid="cart-min-order-notice">
              {isBelowMinimum ? (
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-amber-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-amber-700">Mindestbestellwert: {minOrderValue.toFixed(2)} €</p>
                    <p className="text-xs text-amber-600 mt-0.5">Es fehlen noch {(minOrderValue - totalAmount).toFixed(2)} € (Produktsumme)</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-green-700">✓ Mindestbestellwert erreicht</p>
              )}
            </div>
            
            <p className="text-xs text-slate-400 italic">{s.checkout_payment_hint || ''}</p>
            
            {/* Haupt-Aktionen */}
            <div className="space-y-2">
              <Button 
                onClick={handleCheckout} 
                className="w-full bg-[#0E518D] hover:bg-[#0B4171] text-white rounded-sm uppercase tracking-wide font-medium py-3" 
                data-testid="checkout-button"
              >
                {s.cart_checkout_button || 'Weiter zur Bestellung'}
              </Button>
              
              {/* NEUER BUTTON: Weiter einkaufen */}
              <Button 
                variant="outline" 
                onClick={handleContinueShopping} 
                className="w-full border-[#0E518D] text-[#0E518D] hover:bg-[#0E518D]/5 rounded-sm uppercase tracking-wide font-medium py-3" 
                data-testid="continue-shopping-button"
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Weiter einkaufen
              </Button>
              
              <Button 
                variant="ghost" 
                onClick={clearCart} 
                className="w-full text-slate-500 hover:text-red-500 hover:bg-red-50 rounded-sm uppercase tracking-wide text-xs" 
                data-testid="clear-cart-button"
              >
                <Trash2 className="h-3.5 w-3.5 mr-2" />
                {s.cart_clear_button || 'Warenkorb leeren'}
              </Button>
            </div>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
};
